import { Link } from "react-router-dom";

import { Avatar } from "../../components/Avatar/Avatar";
import {
  Card,
  CardBody,
  CardHeader,
} from "../../components/Card/Card";
import type { ProjectListItem } from "../auth/useAuth";
import styles from "./AccountPage.module.css";

interface Props {
  projects: ProjectListItem[];
  isSuper: boolean;
  /** Max chips to render before collapsing the remainder into "+N more". */
  maxVisible?: number;
}

/**
 * Account › "Your projects" card. Renders the caller's project memberships as
 * gradient-avatar chips (mockup account.html L474-L500). Super admins see a
 * truncated list with a "+N more" link to /projects, since they have access
 * to every project.
 */
export function YourProjectsCard({
  projects,
  isSuper,
  maxVisible = 6,
}: Props) {
  const count = projects.length;
  const subline = isSuper
    ? `${count} ${count === 1 ? "project" : "projects"} · super_admin has access to all.`
    : `${count} ${count === 1 ? "project" : "projects"} you can access.`;

  const visible = projects.slice(0, maxVisible);
  const overflow = Math.max(0, count - visible.length);

  return (
    <Card>
      <CardHeader>
        <div>
          <div className={styles.cardTitle}>Your projects</div>
          <div className={styles.cardSub}>{subline}</div>
        </div>
      </CardHeader>
      <CardBody>
        {count === 0 ? (
          <div className={styles.muted}>
            You don't have access to any projects yet.
          </div>
        ) : (
          <div className={styles.chips} data-testid="project-chips">
            {visible.map((p) => (
              <Link
                key={p.id}
                to={`/projects/${p.id}`}
                className={styles.chip}
              >
                <Avatar name={p.name} size={20} />
                <span className={styles.chipLabel}>{p.name}</span>
              </Link>
            ))}
            {overflow > 0 && (
              <Link to="/projects" className={`${styles.chip} ${styles.chipMore}`}>
                + {overflow} more
              </Link>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
}
